import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Toaster } from './components/ui/sonner';
import { getCurrentUser } from './utils/api';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import EventEditor from './pages/EventEditor';
import DJInterface from './pages/DJInterface';
import GuestInterface from './pages/GuestInterface';
import GuestJoin from './pages/GuestJoin';
import GuestDashboard from './pages/GuestDashboard';
import AdminPanel from './pages/AdminPanel';
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalPage from './pages/LegalPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import NetworkStatus from './components/NetworkStatus';
import LoadingScreen from './components/ui/LoadingScreen';
import '@/App.css';

// Protected Route Component with HttpOnly cookie check
const ProtectedRoute = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(null); // null = loading
  const [userRole, setUserRole] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Try to get current user (will use HttpOnly cookie)
        const response = await getCurrentUser();
        const user = response.data?.user || response.data; // Handle both formats
        setIsAuthenticated(true);
        setUserRole(user?.role);
        
        // Redirection intelligente basée sur le rôle
        if (user?.role === 'admin') {
          // Admin accédant au dashboard → rediriger vers /admin
          if (location.pathname === '/dashboard') {
            navigate('/admin', { replace: true });
          }
        } else {
          // Non-admin accédant à /admin → rediriger vers /dashboard
          if (location.pathname === '/admin') {
            navigate('/dashboard', { replace: true });
          }
        }
      } catch (error) {
        setIsAuthenticated(false);
      }
    };

    checkAuth();
  }, [location.pathname, navigate]);

  // Loading state
  if (isAuthenticated === null) {
    return <LoadingScreen message="Vérification de l'authentification..." />;
  }

  // Not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Authenticated
  return children;
};

function App() {
  return (
    <HelmetProvider>
    <BrowserRouter>
      <NetworkStatus />
      <Toaster 
        position="top-right"
        toastOptions={{
          style: {
            background: '#1e1b4b',
            color: '#e9d5ff',
            border: '1px solid rgba(168, 85, 247, 0.3)'
          }
        }}
      />
      <Routes>
        <Route path="/login" element={<Login />} />
        
        {/* New guest system with join page */}
        <Route path="/guest/:eventId/join" element={<GuestJoin />} />
        <Route path="/guest/:eventId/dashboard" element={<GuestDashboard />} />
        
        {/* Legacy guest interface (by access code) */}
        <Route path="/guest/:accessCode" element={<GuestInterface />} />
        
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <ProtectedRoute>
              <AdminPanel />
            </ProtectedRoute>
          }
        />
        <Route
          path="/events/:eventId"
          element={
            <ProtectedRoute>
              <EventEditor />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dj/:eventId"
          element={
            <ProtectedRoute>
              <DJInterface />
            </ProtectedRoute>
          }
        />
        
        
        {/* Public Pages */}
        <Route path="/about" element={<AboutPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/legal" element={<LegalPage />} />
        <Route path="/faq" element={<FAQPage />} />
        <Route path="/contact" element={<ContactPage />} />
        
        {/* Blog */}
        <Route path="/blog" element={<Blog />} />
        <Route path="/blog/:slug" element={<BlogPost />} />
        
        {/* Homepage - LandingPage component */}
        <Route path="/" element={<LandingPage />} />
      </Routes>
    </BrowserRouter>
    </HelmetProvider>
  );
}

export default App;
